import React, { useMemo } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Alert } from '@mui/material';
import { Stack } from '@mui/system';

interface MetricsTableHeaders {
	metricsData: Record<string, string>[];
	tableType: 'UPLOAD' | 'OPERATION';
}

const metricsHeaders: Record<string, string> = {
	fileName: 'File Name',
	userName: 'User Name',
	organisations: 'Organisation (s)',
	caseId: 'Operation',
	uploadStarted: 'Upload Started',
	uploadComplete: 'Upload Completed',
	uploadTimeSeconds: 'Upload Time (s)',
	uploadSizeBytes: 'Upload Size (bytes)',
	processingStarted: 'Processing Started',
	processingComplete: 'Processing Complete',
	processingTimeSeconds: 'Processing Time (s)',
	totalTimeSeconds: 'Total Time (s)',
	documentsProcessed: 'Documents Processed',
	suspectKnown: 'Suspect Known',
	wordListName: 'Word List Name',
	uploadReasonMessage: 'Upload Reason',
	onBehalfOfName: 'On Behalf Of (name)',
	onBehalfOfEmail: 'On Behalf Of (email',
	operation: 'Operation',
	jobs: 'Jobs',
	firstUpload: 'First Upload',
	totalTime: 'Total Time (s)',
	numUsers: 'Contributing Users',
	totalDocs: 'Documents Processed',
	enrichmentTime: 'Enrichment Time',
	enrichments: 'Enrichments Completed',
	timeToEnrichSeconds: 'Time To Enrich (s)',
};

const MetricsTable: React.FC<MetricsTableHeaders> = ({ metricsData, tableType }) => {
	const headCells: GridColDef[] = useMemo(() => {
		if (!metricsData || metricsData.length === 0) return [];

		return Object.keys(metricsData[0]).map((key) => ({
			field: key,
			headerName: metricsHeaders[key] || key,
			sortable: true,
			flex: 1,
			minWidth: 180,
		}));
	}, [metricsData, metricsHeaders]);

	return (
		<Stack gap={2}>
			<Alert severity="info">
				Some columns are hidden by default. To enable, click the Columns button on the top of the table.
				<br />
				More data may be hidden, scroll right on the table to view
			</Alert>
			<DataGrid
				showToolbar
				rows={Array.from(metricsData)}
				columns={headCells}
				initialState={{
					columns: {
						columnVisibilityModel: {
							id: false,
							wordListName: false,
							uploadReasonMessage: false,
							onBehalfOfName: false,
							onBehalfOfEmail: false,
							suspectKnown: false,
							enrichmentTime: false,
							enrichments: false,
							timeToEnrichSeconds: false,
						},
					},
					pagination: {
						paginationModel: {
							pageSize: 10,
							page: 0,
						},
					},
				}}
				pageSizeOptions={[5, 10, 20, 50]}
				slotProps={{
					toolbar: {
						showQuickFilter: false, // Hides the quick search input
					},
				}}
				getRowId={(row) => {
					return tableType === 'OPERATION' ? row.operation : row.id;
				}}
				disableColumnFilter
			/>
		</Stack>
	);
};

export default MetricsTable;
